import { useTranslation } from '@jobcost-monorepo/i18n';
import { Alert, Icon, Loader } from '@jobcost-monorepo/ui';
import { BASE_URL, MN_TIMESHEET } from '@jobcost-monorepo/crud';
import React, {
  ReactElement,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import SendFormButtons from './Buttons';
import SendFormBody from './Body';
import SendFormTitle from './Title';
import {
  apiClient,
  ITimesheetsReportGlobalPrint,
  TimesheetGlobalPdf,
} from '@jobcost-monorepo/utils';
import ReactPDF from '@react-pdf/renderer';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SendFormSchema } from './validationSchema';

interface ISendFormProps {
  setSendFormOpen: (value: SetStateAction<boolean>) => void;
  groupedDataPrint: ITimesheetsReportGlobalPrint[];
  start: string | undefined;
  end: string | undefined;
}

const SendForm = ({
  setSendFormOpen,
  groupedDataPrint,
  start,
  end,
}: ISendFormProps): ReactElement => {
  const { t } = useTranslation(['timesheets']);
  const methods = useForm({
    resolver: zodResolver(SendFormSchema),
    defaultValues: {
      emails: [{ email: '' }],
      IsEmailForOrdersToSend: false,
      emailOrders: '',
      IsEmailForInvoiceToSend: false,
      emailInvoice: '',
      IsEmailForTimesheetToSend: false,
      emailTimesheet: '',
      customerName: '',
    },
  });
  const { getValues, trigger, clearErrors, setError } = methods;

  const [showPopup, setShowPopup] = React.useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleCloseForm = () => {
    setSendFormOpen(false);
  };

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    clearErrors();
    const emails: { email: string }[] = getValues('emails');

    const fieldsToValidate: Array<
      'emailInvoice' | 'emailOrders' | 'emailTimesheet' | 'customerName'
    > = ['customerName'];
    if (getValues('IsEmailForInvoiceToSend')) {
      fieldsToValidate.push('emailInvoice');
    }
    if (getValues('IsEmailForOrdersToSend')) {
      fieldsToValidate.push('emailOrders');
    }
    if (getValues('IsEmailForTimesheetToSend')) {
      fieldsToValidate.push('emailTimesheet');
    }

    const emptyEmail = fieldsToValidate.length === 1 && emails.length === 1;
    const isValid = await trigger(fieldsToValidate);
    setHasErrors(!isValid);

    if (emptyEmail) {
      setError('emails', { message: 'email.error.empty' });
      setHasErrors(true);
    }

    if (!isValid || emptyEmail) {
      setShowPopup(true);
      return;
    }

    if (!start) {
      setHasErrors(true);
      setShowPopup(true);
      return;
    }

    setLoading(true);

    try {
      emails.shift();
      const blob = await ReactPDF.pdf(
        <TimesheetGlobalPdf
          dataList={groupedDataPrint}
          startDate={start}
          endDate={end}
        />,
      ).toBlob();

      // Converti il Blob in base64 usando FileReader
      const blobToBase64 = (blob: Blob) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result; // Restituisce la stringa Data URL
            if (typeof base64String === 'string') {
              resolve(base64String.split(',')[1]); // Prende solo la parte base64
            } else {
              reject(new Error('Errore nella lettura del Blob'));
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob); // Legge il Blob come una Data URL
        });
      };

      const pdfBase64 = await blobToBase64(blob);

      const emailsToSend = new Set<string>();

      emails.map((email) => {
        emailsToSend.add(email.email);
      });

      if (
        getValues('IsEmailForTimesheetToSend') &&
        getValues('emailTimesheet')
      ) {
        emailsToSend.add(getValues('emailTimesheet'));
      }

      if (getValues('IsEmailForInvoiceToSend') && getValues('emailInvoice')) {
        emailsToSend.add(getValues('emailInvoice'));
      }

      if (getValues('IsEmailForOrdersToSend') && getValues('emailOrders')) {
        emailsToSend.add(getValues('emailOrders'));
      }

      emailsToSend.forEach((email) => {
        sendEmail(email, pdfBase64);
      });
    } catch (e) {
      setHasErrors(true);
      setShowPopup(true);
    }

    setShowPopup(true);
    setLoading(false);
    setTimeout(() => {
      setSendFormOpen(false);
    }, 3000);
  };

  const sendEmail = async (email: string, pdfBase64: unknown) => {
    const props = {
      email: email,
      pdfBlobBase64: pdfBase64,
      startDate: start,
      name: getValues('customerName'),
    };

    const response = await apiClient
      .post<{}>({
        url: `${BASE_URL}/${MN_TIMESHEET.toLowerCase()}/reportGlobalSend`,
        params: props,
      })
      .then(() => {
        console.log({ props });
      });

    console.log({ response });
  };

  useEffect(() => {
    if (showPopup) {
      setTimeout(() => {
        setShowPopup(false);
      }, 3000);
    }
  }, [showPopup]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(ev) => handleSubmit(ev)}
        className="size-full flex flex-col"
      >
        <div className="flex justify-between items-center content-center">
          <SendFormTitle />
          <div
            onClick={handleCloseForm}
            className="bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 rounded-2xl p-1"
          >
            <Icon
              iconName="cancel"
              className="hover:opacity-50 hover:cursor-pointer fill-black stroke-black dark:fill-white dark:stroke-white"
            />
          </div>
        </div>
        <div className="flex flex-grow overflow-hidden flex-col border h-full justify-between py-4 mt-4 rounded-lg border-purple70">
          <Loader showLoader={loading} />
          <SendFormBody methods={methods} />
          {!showPopup && <SendFormButtons handleCloseForm={handleCloseForm} />}
        </div>
        {showPopup && (
          <Alert
            type={hasErrors ? 'error' : 'success'}
            message={hasErrors ? t('email.error.send') : t('email.success')}
          />
        )}
      </form>
    </FormProvider>
  );
};

export default SendForm;
